'use client';

import { useState } from 'react';
import { IconBuildingBank, IconUsers } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { createSpaceId } from 'pc-types';

import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-error';
import { useDialogSubmission } from '@/hooks/use-dialog-submission';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authHeaders, unwrapLambdaResponse } from '@/lib/api';
import { useAccessToken } from '@/lib/auth-client';
import { BACKEND_URL } from '@/lib/constants';
import { isOrgAdmin } from '@/lib/org-roles';
import { reportError } from '@/lib/report-error';
import { useOrgSelf } from '@/lib/use-org-self';
import { cn } from '@/lib/utils';
import type { Space, SpaceKind } from './use-spaces';

function VisibilityCard({
  selected,
  onSelect,
  icon,
  title,
  description,
  disabled = false,
}: {
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={cn(
        'flex items-start gap-3 rounded-md border p-3 text-left transition-colors',
        selected
          ? 'border-line-strong bg-wash-light'
          : 'border-line hover:bg-wash-light',
        disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent'
      )}
    >
      <div className="mt-0.5 text-fg-secondary">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-base font-medium text-fg">{title}</p>
        <p className="text-xs text-fg-tertiary">{description}</p>
      </div>
    </button>
  );
}

export function NewSpaceDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (space: Space) => void;
}) {
  const qc = useQueryClient();
  const { getAccessToken } = useAccessToken();
  const { data: orgSelf } = useOrgSelf();
  // POST /spaces rejects kind 'organization' for non-org-admins; mirror that
  // gate here instead of letting the request fail with a generic error.
  const canCreateOrgSpace = isOrgAdmin(orgSelf?.currentUserRole);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<SpaceKind>('standard');
  // The dialog's own copy of a failure, so closing it clears the message
  // without touching the request, which may still be in flight.
  const [error, setError] = useState<string | null>(null);
  const submission = useDialogSubmission(open);

  const reset = () => {
    setName('');
    setKind('standard');
    setError(null);
  };

  const createSpace = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);
    const result = await submission.submit(async () => {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${BACKEND_URL}/spaces`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ id: createSpaceId(), name: trimmed, kind }),
      });
      if (!res.ok) throw new Error('Failed to create space');
      return unwrapLambdaResponse<{ space: Space }>(await res.json());
    });
    if (!result) return;
    if (result.ok) {
      const { space } = result.value;
      qc.invalidateQueries({ queryKey: ['spaces'] });
      if (result.current) {
        onOpenChange(false);
        reset();
        onCreated?.(space);
      } else {
        // Closed mid-request: the form now belongs to whatever comes next.
        toast.success(
          `Created “${space.name}”`,
          onCreated
            ? { action: { label: 'Open', onClick: () => onCreated(space) } }
            : undefined
        );
      }
      return;
    }
    reportError(result.error, { site: 'space_create' });
    if (result.current) setError("Couldn't create the space. Try again.");
    else toast.error(`Couldn't create the space “${trimmed}”`);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New space</DialogTitle>
          <DialogDescription>
            A space is a top-level container with its own access. Folders live
            inside it.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="space-name">Name</Label>
              <Input
                id="space-name"
                autoFocus
                placeholder="e.g. Project Osprey"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-8"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void createSpace();
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Who can access it?</Label>
              <VisibilityCard
                selected={kind === 'standard'}
                onSelect={() => setKind('standard')}
                icon={<IconUsers className="size-5" />}
                title="Invited people only"
                description="Only people you add can see this space. Best for deals and client work."
              />
              <VisibilityCard
                selected={kind === 'organization'}
                onSelect={() => setKind('organization')}
                disabled={!canCreateOrgSpace}
                icon={<IconBuildingBank className="size-5" />}
                title="Everyone at your firm"
                description={
                  canCreateOrgSpace
                    ? 'Anyone at your firm can find and open it. Best for templates, brand assets, and shared references.'
                    : 'Only organization admins can create firm-wide spaces.'
                }
              />
            </div>
            <FormError>{error}</FormError>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => handleOpenChange(false)}
            disabled={submission.pending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void createSpace()}
            disabled={!name.trim() || submission.pending}
          >
            {submission.pending ? 'Creating…' : 'Create space'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
